import Image from "next/image";
import Link from "next/link";
import React from "react";

export type BlogCardPost = {
  slug: string
  title: string
  metaDesc: string
  socialImage: string
  date: string | null
}

export const isExternalImage = (imageUrl: string) =>
  imageUrl.startsWith("http://") || imageUrl.startsWith("https://");

// External URLs are used as is; anything else is a path under /public
export const getImageSrc = (imageUrl: string) =>
  isExternalImage(imageUrl) ? imageUrl : `/${imageUrl}`;

// UTC keeps server and client output identical, so hydration never mismatches
export const formatPostDate = (date: string | null) =>
  date
    ? new Date(date).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric", timeZone: "UTC"})
    : "";

type PostCardProps = {
  post: BlogCardPost
  // Above-the-fold cards load eagerly, the rest lazily
  priority?: boolean
  // Rendered width hint for next/image; defaults to the 3-column article grid
  sizes?: string
}

export const PostCard = ({
  post,
  priority = false,
  sizes = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
}: PostCardProps) => {
  const {slug, title, metaDesc, socialImage, date} = post;

  return <Link
    href={`/blog/${slug}`}
    className={[
      "group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white",
      "transition duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg",
    ].join(" ")}
  >
    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
      {socialImage ? (
        isExternalImage(socialImage) ? (
          <img
            src={socialImage}
            alt={title}
            loading={priority ? "eager" : "lazy"}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <Image
            src={getImageSrc(socialImage)}
            alt={title}
            fill
            sizes={sizes}
            priority={priority}
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )
      ) : (
        <div className="flex h-full items-center justify-center p-6 text-center text-lg font-semibold capitalize text-blue-300">
          NotionSender
        </div>
      )}
    </div>

    <div className="flex flex-1 flex-col gap-2 p-5">
      <h2 className="line-clamp-2 text-lg font-semibold leading-snug text-gray-900">{title}</h2>
      <p className="line-clamp-3 text-sm text-gray-600">{metaDesc}</p>
      <div className="mt-auto flex items-center justify-between gap-3 pt-3 text-sm">
        <span className="text-gray-500">{formatPostDate(date)}</span>
        <span className="font-semibold text-blue-500 transition group-hover:translate-x-0.5">Read article →</span>
      </div>
    </div>
  </Link>
}
